import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { X, Eye } from "lucide-react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { t } from "../lib/i18n";

/**
 * Fullscreen story viewer with progress bar.
 * Supports both image (6s) and video (up to 5s, auto-plays muted).
 */
export default function StoryView() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const lang = user?.language || "ru";
  const [story, setStory] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const IMAGE_DURATION_MS = 6000;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/stories/user/${userId}`);
        if (!cancelled) setStory(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "not_found");
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    if (!story) return;
    if (story.media_type === "video") {
      const v = videoRef.current;
      if (!v) return;
      const update = () => {
        if (v.duration > 0) setProgress(v.currentTime / v.duration);
      };
      const onEnd = () => nav(-1);
      v.addEventListener("timeupdate", update);
      v.addEventListener("ended", onEnd);
      v.play().catch(() => {});
      return () => {
        v.removeEventListener("timeupdate", update);
        v.removeEventListener("ended", onEnd);
      };
    }
    // Image: use interval
    const start = Date.now();
    timerRef.current = setInterval(() => {
      const p = (Date.now() - start) / IMAGE_DURATION_MS;
      if (p >= 1) {
        clearInterval(timerRef.current);
        nav(-1);
      } else {
        setProgress(p);
      }
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [story, nav]);

  if (error) {
    return (
      <div className="kupidon-shell bg-black text-white flex flex-col items-center justify-center safe-top safe-bottom">
        <div className="text-white/80 mb-4">{t(lang, "story_none")}</div>
        <button
          data-testid="story-view-close"
          onClick={() => nav(-1)}
          className="btn-pill bg-white/15 text-white px-5"
        >
          {t(lang, "cancel")}
        </button>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="kupidon-shell bg-black flex items-center justify-center">
        <div className="text-white/70">{t(lang, "loading")}</div>
      </div>
    );
  }

  const isVideo = story.media_type === "video";

  return (
    <div className="kupidon-shell bg-black relative overflow-hidden" data-testid="story-view">
      <div className="absolute top-3 left-3 right-14 z-20 h-1 bg-white/25 rounded-full overflow-hidden safe-top">
        <div
          className="h-full bg-white"
          style={{ width: `${progress * 100}%`, transition: isVideo ? "none" : "width 50ms linear" }}
        />
      </div>
      <button
        data-testid="story-view-close"
        onClick={() => nav(-1)}
        className="absolute top-2 right-3 z-20 w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white safe-top"
        aria-label="close"
      >
        <X className="w-5 h-5" />
      </button>

      {isVideo ? (
        <video
          ref={videoRef}
          key={story.id}
          src={story.data_url}
          muted
          playsInline
          autoPlay
          className="w-full h-full object-cover absolute inset-0"
        />
      ) : (
        <motion.img
          key={story.id}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          src={story.data_url}
          alt=""
          className="w-full h-full object-cover absolute inset-0"
        />
      )}

      <div className="absolute bottom-0 left-0 right-0 p-5 pb-8 z-10 bg-gradient-to-t from-black/80 to-transparent text-white safe-bottom">
        {story.caption && (
          <div className="text-lg font-[Onest] font-semibold leading-tight mb-2">
            {story.caption}
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-white/70">
          <Eye className="w-3.5 h-3.5" />
          {story.views ?? 0}
        </div>
      </div>
    </div>
  );
}
